/**
 * Builder-side glue for the shared public-profile link contracts.
 *
 * `@tabbio/utils/public-profile-links` owns the parsing rules; this module only
 * decides how a field is *presented* in the two editor sheets - which error
 * string it shows, and which placeholder goes in the editable part once a fixed
 * prefix ("linkedin.com/in/") is rendered beside it.
 */
import {
  PUBLIC_PROFILE_WEBSITE_FIELD,
  getPublicProfileContactField,
  getPublicProfileSocialField,
  type PublicProfileLinkField,
  type PublicProfileLinkMode,
} from '@tabbio/utils/public-profile-links';
import type {
  PublicProfileContactChannelId,
  PublicProfileSocialId,
} from '@tabbio/types';

export {
  filterPublicProfileLinkInput,
  isPublicProfileLinkInputValid,
  splitPublicProfileLink,
  toStoredPublicProfileLink,
  type PublicProfileLinkField,
} from '@tabbio/utils/public-profile-links';

export function getSocialLinkField(
  id: PublicProfileSocialId,
  mode: PublicProfileLinkMode,
): PublicProfileLinkField {
  return getPublicProfileSocialField(id, mode);
}

/** The company's own website/careers link, edited in the Add link sheet. */
export function getWebsiteLinkField(): PublicProfileLinkField {
  return PUBLIC_PROFILE_WEBSITE_FIELD;
}

export function getContactLinkField(
  id: PublicProfileContactChannelId,
): PublicProfileLinkField {
  return getPublicProfileContactField(id);
}

/**
 * Placeholder for the editable part of the pill.
 *
 * Handle fields need a translated word ("username"), since the fixed prefix
 * beside them already carries the platform. Phone, email and URL examples are
 * symbols and hosts, so the field's own example is used verbatim.
 */
export function getLinkPlaceholder(
  field: PublicProfileLinkField,
  tx: (key: string) => string,
): string {
  return field.kind === 'handle'
    ? tx('publicProfile.linkPlaceholders.handle')
    : field.example;
}

/**
 * i18n key for "what you typed is not a valid <thing>". Slack gets its own
 * string because "Enter a full https:// link" does not tell the user the link
 * has to be a Slack one.
 */
export function getLinkErrorKey(
  field: PublicProfileLinkField,
  id: string,
): string {
  if (id === 'slack') return 'publicProfile.linkErrors.slack';
  switch (field.kind) {
    case 'phone':
      return 'publicProfile.linkErrors.phone';
    case 'email':
      return 'publicProfile.linkErrors.email';
    case 'handle':
      return 'publicProfile.linkErrors.handle';
    default:
      return 'publicProfile.linkErrors.url';
  }
}
